import * as React from 'react';
import { useState } from 'react';
import { Typography, Skeleton, emptyFragment } from '@execonline-inc/execonline-ui';
import { DashboardSummaryPayload } from '../../../DashboardSummaryStore/Types';
import MarkdownRenderer from '../../DashboardSummary/MarkdownRenderer';
import AIRationale from '../../DashboardSummary/AIRationale';
import RefreshIcon from '../../Cutesies/RefreshIcon';
import CopyRoundedIcon from '../../Cutesies/CopyRoundedIcon';
import Checkmark from '../../Cutesies/Checkmark';
import {
  formatDateRange,
  formatGeneratedTime,
  getDefaultDateRange,
  copySummaryToClipboard,
} from '../../DashboardSummary/utils';
import { T } from '../../../Translations';

interface Props {
  summary: DashboardSummaryPayload | null;
  isLoading: boolean;
  error: string | null;
  emptyMessage?: string | null;
  onRefresh?: () => void;
}

const SummaryTabContent: React.FC<Props> = ({
  summary,
  isLoading,
  error,
  emptyMessage,
  onRefresh,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = async () => {
    if (!summary) return;

    const success = await copySummaryToClipboard(
      summary.headline,
      summary.summary,
      summary.rationale,
    );
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  // Show loading skeleton for both loading state and initial idle state (before data loads)
  if (isLoading || (!summary && !error && !emptyMessage)) {
    return (
      <div className="space-y-4">
        <Skeleton variant="text" className="w-3/4 h-4" />
        <Skeleton variant="text" className="w-full h-4" />
        <Skeleton variant="text" className="w-full h-4" />
        <Skeleton variant="text" className="w-2/3 h-4" />
        <Skeleton variant="text" className="w-full h-20 mt-4" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-sm">
        <Typography variant="bodyMedium" color="red">
          {error}
        </Typography>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="mt-4 text-teal-600 hover:text-teal-700 text-sm font-medium"
          >
            <T kind="Try again" />
          </button>
        )}
      </div>
    );
  }

  if (emptyMessage) {
    return (
      <div className="text-gray-500 text-sm">
        <Typography variant="bodyMedium">{emptyMessage}</Typography>
      </div>
    );
  }

  // This should not be reached due to the loading check above, but kept as safety fallback
  if (!summary) {
    return (
      <div className="space-y-4">
        <Skeleton variant="text" className="w-3/4 h-4" />
        <Skeleton variant="text" className="w-full h-4" />
        <Skeleton variant="text" className="w-full h-4" />
        <Skeleton variant="text" className="w-2/3 h-4" />
        <Skeleton variant="text" className="w-full h-20 mt-4" />
      </div>
    );
  }

  const defaults = getDefaultDateRange();
  const startDate = summary.filters.startDate.getOrElse(() => defaults.startDate);
  const endDate = summary.filters.endDate.getOrElse(() => defaults.endDate);
  const dateRange = formatDateRange(startDate, endDate);
  const generatedTime = formatGeneratedTime(summary.metadata.generatedAt);

  return (
    <div className="space-y-4">
      {/* Meta info and actions */}
      <div className="flex items-center justify-between">
        <Typography variant="caption" className="text-gray-500">
          {dateRange && `${dateRange} • `}
          {generatedTime}
        </Typography>

        {/* Action buttons */}
        <div className="flex items-center space-x-1">
          {/* Refresh button */}
          <button
            onClick={onRefresh}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100/30 rounded-lg transition-colors"
            title="Regenerate summary"
          >
            <RefreshIcon className="w-5 h-5" />
          </button>

          {/* Copy button */}
          <button
            onClick={handleCopy}
            className={`p-2 rounded-lg transition-colors ${
              copySuccess
                ? 'text-green-600 bg-green-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/30'
            }`}
            title={copySuccess ? 'Copied!' : 'Copy to clipboard'}
          >
            {copySuccess ? (
              <Checkmark className="w-5 h-5 fill-current" />
            ) : (
              <CopyRoundedIcon className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Headline */}
      {summary.headline
        .map((h) => <p className="text-green-600 text-base font-medium">{h}</p>)
        .getOrElse(emptyFragment)}

      {/* Summary content */}
      <MarkdownRenderer content={summary.summary} expandable={false} />

      {/* AI Rationale section */}
      <div className="mt-4">
        <AIRationale items={summary.rationale.getOrElse(() => [])} />
      </div>
    </div>
  );
};

export default SummaryTabContent;
